import React, { useEffect, useRef, useState } from "react";
import { IoIosSend } from "react-icons/io";
import { IoMdArrowRoundBack } from "react-icons/io";
import axios from "axios";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import img from "../../../imgs/hero.png";
import backgroundImg from "../../../imgs/ChatPg.jpg";
import { Config } from "../../../API/config";
import { MessageType } from "../../../types/chat";

interface adminData {
    _id: string;
    name: string;
    profileImage: string;
}

interface User {
    _id: string;
    name: string;
    phone: string;
    profileImage: string;
    role: string;
}

interface Chat {
    _id: string;
    participants: string[];
    createdAt: string;
    updatedAt: string;
    __v: number;
    allParticipants: User[];
}

interface Message {
    _id: string;
    content: string;
    senderId: string;
    receiverId: string;
    status: "approved" | "pending" | "rejected" | "sent";
    read: boolean;
    adminId?: adminData;
    createdAt: string;
    updatedAt: string;
}

interface ChatMessages {
    _id: string;
    participants: string[];
    messages: Message[];
    createdAt: string;
    updatedAt: string;
    __v: number;
}

const ChatAdmin = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const [cookies] = useCookies(["user-data", "token"]);
    const senderId = cookies["user-data"]?.id;
    const adminId: adminData = cookies["user-data"];
    const chat: Chat = location.state?.chat;
    const chatId = chat._id;

    const [message, setMessage] = useState("");
    const [ChatMessages, setChatMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [ChatMessages]);

    useEffect(() => {
        if (!chatId) return;

        const fetchMessages = async () => {
            setIsLoading(true);
            try {
                const { data } = await axios.get(`${Config.getChatById}/${chatId}`, { withCredentials: true });
                setChatMessages(data.data.messages.reverse());
                console.log(data);
            } catch (err) {
                console.error("Error fetching messages", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMessages();
    }, [chatId]);

    const handleSend = async () => {
        if (message.trim() === "") return;

        const tempMessage: Message = {
            _id: `temp-${Math.random().toString(36).substring(7)}`,
            content: message,
            senderId: senderId, // Set as the current user (admin)
            receiverId: chat.allParticipants.find(p => p._id !== senderId)?._id || "",
            status: "pending",
            adminId: adminId, // Include adminId to mark as admin message
            read: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        // Optimistically add the temporary message to the UI
        setChatMessages((prevMessages) => [...prevMessages, tempMessage]);
        setMessage("");

        try {
            const { data } = await axios.post(
                `${Config.sendMessageByAdmin}/${chat.allParticipants[1]._id}/${chat.allParticipants[0]._id}`,
                { content: message },
                { withCredentials: true }
            );

            if (data.statusCode === 200) {
                // Update the temporary message with server response, preserving adminId
                setChatMessages((prevMessages) =>
                    prevMessages.map((msg) =>
                        msg._id === tempMessage._id
                            ? { ...data.message, adminId: tempMessage.adminId, status: "sent" }
                            : msg
                    )
                );
            }
        } catch (err) {
            console.error("Message send failed", err);
            // Optionally mark the message as failed
            setChatMessages((prevMessages) =>
                prevMessages.map((msg) =>
                    msg._id === tempMessage._id ? { ...msg, status: "rejected" } : msg
                )
            );
        }
    };

    return (
        <div
            className="h-screen flex flex-col bg-gray-100"
            style={{
                backgroundImage: `url(${backgroundImg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        >
            {!chat ? (
                <div className="text-center text-red-500 mt-10 text-xl">chat data not found.</div>
            ) : (
                <>
                    {/* Header */}
                    {isLoading ? (
                        <div className="bg-white p-3 flex items-center shadow-md animate-pulse">
                            <div className="p-2 text-gray-700">
                                <div className="w-6 h-6 bg-gray-300 rounded-full"></div>
                            </div>
                            <div className="w-10 h-10 bg-gray-300 rounded-full mx-3"></div>
                            <div className="w-32 h-5 bg-gray-300 rounded-lg"></div>
                        </div>
                    ) : (
                        <div className="bg-white p-3 flex items-center shadow-md">
                            <button className="p-2 text-gray-700" onClick={() => navigate(-1)}>
                                <IoMdArrowRoundBack size={22} />
                            </button>
                            <div className="relative w-12 h-10 mr-3">
                                <img
                                    src={chat.allParticipants[0].profileImage || img}
                                    alt="Avatar 1"
                                    className="w-8 h-8 rounded-full absolute left-0 top-1/2 -translate-y-1/2 z-10 border-2 border-white"
                                />
                                <img
                                    src={chat.allParticipants[1].profileImage || img}
                                    alt="Avatar 2"
                                    className="w-8 h-8 rounded-full absolute left-4 top-1/2 -translate-y-1/2 z-0 border-2 border-white"
                                />
                            </div>
                            <span className="text-lg font-semibold">
                                {chat.allParticipants[0].name}, {chat.allParticipants[1].name}
                            </span>
                        </div>
                    )}

                    {/* Chat Messages */}
                    <div className="flex-1 p-4 space-y-4 overflow-y-auto relative bg-white bg-opacity-90">
                        {isLoading ? (
                            <div className="space-y-6">
                                {[...Array(5)].map((_, index) => (
                                    <div key={index} className="flex flex-col animate-pulse gap-4">
                                        <div className="flex justify-end">
                                            <div className="max-w-[75%] p-3 rounded-lg bg-blue-200 rounded-br-none">
                                                <div className="w-40 h-6 bg-gray-300 rounded-lg mb-2"></div>
                                                <div className="w-16 h-3 bg-gray-200 rounded-lg"></div>
                                            </div>
                                        </div>
                                        <div className="flex justify-start">
                                            <div className="max-w-[75%] p-3 rounded-lg bg-gray-200 rounded-bl-none">
                                                <div className={`${index % 2 === 0 ? "w-32" : "w-48"} h-6 bg-gray-300 rounded-lg mb-2`}></div>
                                                <div className="w-16 h-3 bg-gray-200 rounded-lg"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : ChatMessages.length === 0 ? (
                            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center text-gray-700 text-lg shadow-md p-4 bg-gray-300 p-2 rounded-lg">
                                ارسل رسالة لتبدأ المحادثة
                            </div>
                        ) : (
                            <>
                                {ChatMessages.map((message) => {
                                    const isSender = message.senderId === senderId || (message.adminId && message.adminId._id === senderId);
                                    const sender = chat.allParticipants.find((p) => p._id === message.senderId) || (message.adminId ? { _id: message.adminId._id, name: message.adminId.name, profileImage: message.adminId.profileImage } : null);

                                    return isSender ? (
                                        <div key={message._id} className="flex flex-col items-end">
                                            <div className="max-w-[75%] p-3 rounded-lg shadow-md text-sm bg-blue-500 text-white rounded-br-none relative">
                                                <p>{message.content}</p>
                                                <span className="block text-xs text-gray-300 mt-1 text-right">
                                                    {new Date(message.createdAt).toLocaleTimeString("ar-EG", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}{" "}
                                                    {message.status === "pending" ? "⏳" : message.read ? "✔✔" : "✔"}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div key={message._id} className="flex flex-col items-start">
                                            <div className="flex items-center gap-2 mb-1">
                                                <img
                                                    src={sender?.profileImage || img}
                                                    alt={sender?.name || "User"}
                                                    className="w-6 h-6 rounded-full"
                                                />
                                                <span className="text-sm font-medium text-gray-700">{sender?.name || "Unknown"}</span>
                                            </div>
                                            <div className="max-w-[75%] p-3 rounded-lg shadow-md text-sm bg-gray-200 text-gray-900 rounded-bl-none">
                                                <p>{message.content}</p>
                                                <span className="block text-xs text-gray-500 mt-1 text-right">
                                                    {new Date(message.updatedAt).toLocaleTimeString("ar-EG", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="bg-white border-t p-3 flex items-center w-full animate-pulse">
                            <div className="flex-1 w-full min-w-0 px-4 py-3 border rounded-full bg-gray-200"></div>
                            <div className="ml-3 w-12 h-12 bg-gray-300 rounded-full"></div>
                        </div>
                    ) : (
                        <div className="bg-white border-t p-3 flex items-center w-full">
                            <input
                                type="text"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                placeholder="اكتب رسالتك..."
                                className="flex-1 w-full min-w-0 px-4 py-3 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm shadow-sm"
                            />
                            <button
                                onClick={handleSend}
                                className="ml-3 bg-blue-500 text-white p-3 rounded-full hover:bg-blue-600 transition flex items-center justify-center shadow-md"
                            >
                                <IoIosSend size={22} />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ChatAdmin;